import React from 'react';

import { useTranslation } from 'react-i18next';

import { SelfTestResult } from 'config/api/selfTests/_types';
import parseDate from 'helpers/parseDate';
import general_messages from 'messages/general_messages';
import self_test_messages from 'messages/self_test_messages';

const PRINT_STYLES = `
.ps-self-test-print { background: #fff; color: #000; padding: 16px; font-size: 14px; line-height: 1.45; }
.ps-self-test-print h1 { font-size: 24px; margin: 0 0 16px; }
.ps-self-test-print h2 { font-size: 18px; margin: 24px 0 8px; break-after: avoid; }
.ps-self-test-print h3 { font-size: 16px; margin: 0 0 8px; break-after: avoid; }
.ps-self-test-print__meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 20px; margin-bottom: 20px; }
.ps-self-test-print__label { font-weight: 700; }
.ps-self-test-print__question { border: 1px solid #000; padding: 12px; margin: 0 0 16px; break-inside: avoid; page-break-inside: avoid; }
.ps-self-test-print__description { margin: 6px 0 12px; white-space: pre-wrap; }
.ps-self-test-print__answer { border-top: 1px solid #999; padding-top: 8px; margin-top: 8px; }
@media print {
  .ps-self-test-print { padding: 0; }
  .ps-self-test-print__meta { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
`;

type Props = {
  result: SelfTestResult;
};

const SelfTestUserInstancePrintTemplate: React.FC<Props> = ({ result }) => {
  const { t } = useTranslation();
  const displayName = result.displayName || result.templateHeading || t(self_test_messages.self_test_title);
  const hasCustomTitle = !!result.title;

  const metadata: Array<[any, React.ReactNode]> = [
    [self_test_messages.progress_title, displayName],
    [self_test_messages.progress_asset, result.assetName || t(general_messages.no_data)],
    [self_test_messages.results, `${result.currentScore} / ${result.maxScore}`],
    [self_test_messages.completed_at, result.completedAt ? parseDate(result.completedAt) : '-'],
  ];
  if (hasCustomTitle) {
    metadata.splice(1, 0, [self_test_messages.template_name, result.templateHeading || '-']);
  }

  return (
    <article className='ps-self-test-print'>
      <style>{PRINT_STYLES}</style>
      <h1>{displayName}</h1>
      <section className='ps-self-test-print__meta'>
        {metadata.map(([label, value]) => (
          <div key={`${label[0]}-${value}`}>
            <span className='ps-self-test-print__label'>{t(label)}: </span>
            <span>{value || '-'}</span>
          </div>
        ))}
      </section>

      {result.feedback && (
        <section>
          <h2>{result.feedback.heading}</h2>
          {result.feedback.description && <div className='ps-self-test-print__description'>{result.feedback.description}</div>}
        </section>
      )}

      <h2>{t(self_test_messages.results)}</h2>
      {result.questions.map(question => (
        <section key={question.questionId} className='ps-self-test-print__question'>
          <h3>{question.heading}</h3>
          {question.description && <div className='ps-self-test-print__description'>{question.description}</div>}
          <div className='ps-self-test-print__answer'>
            <span className='ps-self-test-print__label'>{t(self_test_messages.selected_answer)}: </span>
            <span>{question.selectedAnswer?.heading || t(self_test_messages.no_answer)}</span>
          </div>
          {question.selectedAnswer?.description && (
            <div className='ps-self-test-print__description'>{question.selectedAnswer.description}</div>
          )}
          {question.recommendationText && (
            <div className='ps-self-test-print__description'>
              <span className='ps-self-test-print__label'>{t(self_test_messages.recommendations)}: </span>
              {question.recommendationText}
            </div>
          )}
        </section>
      ))}
    </article>
  );
};

export default SelfTestUserInstancePrintTemplate;
